import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button } from '@img/ui';
import { api, type ApiSuccess } from '../../lib/api';

interface FalLicenseItem {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string;
  falLicenseNumber: string | null;
  falLicenseStatus: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED' | null;
  falLicenseExpiresAt: string | null;
  falLicenseUploadedAt: string | null;
  falLicenseReviewedAt: string | null;
  falLicenseDocumentUrl: string | null;
  falLicenseRejectionReason: string | null;
}

interface ListResponse {
  items: FalLicenseItem[];
  stats: Record<string, number>;
}

const STATUS: Record<string, { label: string; color: 'amber' | 'green' | 'red' | 'gray'; icon: string }> = {
  PENDING:  { label: 'قيد المراجعة', color: 'amber', icon: '⏳' },
  APPROVED: { label: 'معتمدة',        color: 'green', icon: '✓' },
  REJECTED: { label: 'مرفوضة',        color: 'red',   icon: '✕' },
  EXPIRED:  { label: 'منتهية',        color: 'gray',  icon: '⌛' },
};

export function AdminFalLicensesPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'fal-licenses', statusFilter],
    queryFn: () => api.get<ApiSuccess<ListResponse>>('/api/admin/fal-licenses/admin', {
      query: statusFilter ? { status: statusFilter } : {},
    }),
    refetchInterval: 60_000,
  });

  const items = data?.data?.items ?? [];
  const stats = data?.data?.stats ?? {};

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'fal-licenses'] });

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-black text-navy-dark">🪪 رخص فال</h1>
        <p className="text-sm text-ink-muted mt-1">
          مراجعة رخص فال المرفوعة من المستخدمين — التحقق من صلاحيتها على
          {' '}
          <a href="https://eservicesredp.rega.gov.sa/auth/queries/Brokerage" target="_blank" rel="noreferrer" className="text-navy-dark underline font-bold">
            بوابة الهيئة العامة للعقار
          </a>
        </p>
      </div>

      {/* 📊 شارات الحالات */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatusCard label="قيد المراجعة" value={stats['PENDING'] ?? 0}  color="amber" icon="⏳"
          active={statusFilter === 'PENDING'}  onClick={() => setStatusFilter(statusFilter === 'PENDING' ? '' : 'PENDING')} pulse />
        <StatusCard label="معتمدة"         value={stats['APPROVED'] ?? 0} color="green" icon="✓"
          active={statusFilter === 'APPROVED'} onClick={() => setStatusFilter(statusFilter === 'APPROVED' ? '' : 'APPROVED')} />
        <StatusCard label="مرفوضة"         value={stats['REJECTED'] ?? 0} color="red"   icon="✕"
          active={statusFilter === 'REJECTED'} onClick={() => setStatusFilter(statusFilter === 'REJECTED' ? '' : 'REJECTED')} />
        <StatusCard label="منتهية"         value={stats['EXPIRED'] ?? 0}  color="gray"  icon="⌛"
          active={statusFilter === 'EXPIRED'}  onClick={() => setStatusFilter(statusFilter === 'EXPIRED' ? '' : 'EXPIRED')} />
      </div>

      {/* القائمة */}
      {isLoading ? (
        <div className="text-center py-12 text-ink-muted">⟳ جاري التحميل…</div>
      ) : items.length === 0 ? (
        <Card><CardBody>
          <div className="text-center py-12 text-ink-muted">
            <div className="text-5xl mb-3">🪪</div>
            <p>لا توجد رخص {STATUS[statusFilter]?.label ?? ''} حالياً</p>
          </div>
        </CardBody></Card>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <LicenseRow key={item.id} item={item} onOpen={() => setOpenId(item.id)} />
          ))}
        </div>
      )}

      {/* مودال المراجعة */}
      {openId && (
        <ReviewModal
          item={items.find((i) => i.id === openId)!}
          onClose={() => setOpenId(null)}
          onUpdated={invalidate}
        />
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ بطاقة الرخصة ━━━━━━━━━━━━━━━━━━━━

function LicenseRow({ item, onOpen }: { item: FalLicenseItem; onOpen: () => void }) {
  const status = STATUS[item.falLicenseStatus ?? ''];
  const expired = item.falLicenseExpiresAt && new Date(item.falLicenseExpiresAt) < new Date();
  const daysUntilExpiry = item.falLicenseExpiresAt
    ? Math.ceil((new Date(item.falLicenseExpiresAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000))
    : null;

  return (
    <Card className={item.falLicenseStatus === 'PENDING' ? 'border-amber-300 bg-amber-50/30' : ''}>
      <CardBody>
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-12 h-12 rounded-xl bg-navy-soft flex items-center justify-center text-2xl shrink-0">
            🪪
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap mb-1">
              <h3 className="font-black text-navy-dark">{item.name}</h3>
              <Badge color={item.role === 'BROKER' ? 'green' : 'navy'}>{item.role}</Badge>
              {status && <Badge color={status.color}>{status.icon} {status.label}</Badge>}
              {expired && item.falLicenseStatus === 'APPROVED' && (
                <Badge color="red">⌛ منتهية</Badge>
              )}
            </div>
            <div className="text-xs text-ink-muted mb-2">
              {item.email && <div dir="ltr">📧 {item.email}</div>}
              {item.phone && <div dir="ltr">📱 {item.phone}</div>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs mb-2">
              <Field label="رقم الرخصة" value={item.falLicenseNumber ?? '—'} dir="ltr" />
              <Field label="تاريخ الانتهاء" value={item.falLicenseExpiresAt ? new Date(item.falLicenseExpiresAt).toLocaleDateString('ar-SA') : '—'} />
              <Field label="تاريخ الرفع" value={item.falLicenseUploadedAt ? new Date(item.falLicenseUploadedAt).toLocaleDateString('ar-SA') : '—'} />
            </div>

            {/* تنبيه قرب الانتهاء */}
            {daysUntilExpiry !== null && daysUntilExpiry > 0 && daysUntilExpiry <= 30 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-[11px] text-amber-800 mb-2">
                ⚠️ الرخصة تنتهي خلال {daysUntilExpiry} يوم — أبلغ المستخدم بالتجديد
              </div>
            )}

            {item.falLicenseStatus === 'REJECTED' && item.falLicenseRejectionReason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-[11px] text-red-800">
                <b>سبب الرفض السابق:</b> {item.falLicenseRejectionReason}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 shrink-0">
            <Button size="sm" variant="primary" onClick={onOpen}>👁️ مراجعة</Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ مودال المراجعة ━━━━━━━━━━━━━━━━━━━━

function ReviewModal({ item, onClose, onUpdated }: {
  item: FalLicenseItem;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const [verified, setVerified] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const approve = useMutation({
    mutationFn: () => api.post(`/api/admin/fal-licenses/admin/${item.id}/approve`, {}),
    onSuccess: () => { onUpdated(); onClose(); },
  });

  const reject = useMutation({
    mutationFn: () => api.post(`/api/admin/fal-licenses/admin/${item.id}/reject`, { reason: rejectReason.trim() }),
    onSuccess: () => { onUpdated(); onClose(); },
  });

  const isPending = item.falLicenseStatus === 'PENDING';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-navy-darker to-navy-dark text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-2xl">🪪</div>
            <div className="min-w-0">
              <h2 className="font-black text-lg truncate">{item.name}</h2>
              <p className="text-xs text-white/70" dir="ltr">{item.email ?? item.phone}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xl shrink-0">✕</button>
        </div>

        <div className="p-6 space-y-4">
          {/* بيانات الرخصة */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="رقم رخصة فال" value={item.falLicenseNumber ?? '—'} dir="ltr" copyable highlight />
            <Field label="تاريخ الانتهاء" value={item.falLicenseExpiresAt ? new Date(item.falLicenseExpiresAt).toLocaleDateString('ar-SA') : '—'} />
            <Field label="الدور" value={item.role} />
            <Field label="تاريخ الرفع" value={item.falLicenseUploadedAt ? new Date(item.falLicenseUploadedAt).toLocaleString('ar-SA') : '—'} />
          </div>

          {/* مستند الرخصة */}
          {item.falLicenseDocumentUrl ? (
            <div>
              <div className="text-xs font-bold text-ink-muted mb-1">📄 مستند الرخصة</div>
              <a href={item.falLicenseDocumentUrl} target="_blank" rel="noreferrer"
                 className="block p-3 rounded-xl bg-navy-soft hover:bg-navy/10 border border-navy/20 text-sm font-bold text-navy-dark text-center">
                📂 فتح المستند المرفوع
              </a>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
              ⚠️ لم يرفع المستخدم أي مستند للرخصة — اطلب منه إرفاق نسخة قبل الاعتماد
            </div>
          )}

          {/* رابط التحقق الرسمي */}
          {isPending && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <div className="text-xs font-bold text-blue-900 mb-2">🔍 التحقق من الرخصة:</div>
              <a
                href={`https://eservicesredp.rega.gov.sa/auth/queries/Brokerage`}
                target="_blank"
                rel="noreferrer"
                className="block p-2.5 rounded-lg bg-white border border-blue-300 hover:bg-blue-100 transition text-center text-sm font-bold text-blue-900"
              >
                🪪 الذهاب لخدمة "الاستعلام عن وسيط عقاري" — الهيئة العامة للعقار
              </a>
              <div className="mt-2 text-[11px] text-blue-800 leading-relaxed">
                ادخل رقم الرخصة <code dir="ltr" className="px-1.5 py-0.5 bg-white rounded">{item.falLicenseNumber}</code> هناك،
                وتحقق من:
                <ul className="mt-1 mr-3 space-y-0.5">
                  <li>• اسم المعلن في الموقع يطابق <b>{item.name}</b></li>
                  <li>• الرخصة سارية وغير منتهية</li>
                  <li>• الرخصة غير معلّقة أو ملغاة</li>
                </ul>
              </div>
            </div>
          )}

          {/* تأكيد التحقق */}
          {isPending && (
            <label className={`flex items-start gap-2 p-3 rounded-xl border-2 cursor-pointer transition ${
              verified ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
            }`}>
              <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} className="w-5 h-5 accent-green-600 mt-0.5" />
              <div>
                <div className={`text-sm font-bold ${verified ? 'text-green-800' : 'text-ink'}`}>
                  ✓ تحققت من الرخصة على البوابة الرسمية
                </div>
                <div className="text-[11px] text-ink-muted">
                  أتحمّل مسؤولية صحة المعلومات والتحقق من المستندات قبل الاعتماد
                </div>
              </div>
            </label>
          )}

          {/* السجل (لو تمت المراجعة) */}
          {!isPending && item.falLicenseReviewedAt && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs">
              <div className="font-bold text-ink-muted mb-1">📋 سجل المراجعة:</div>
              <div>
                تمت المراجعة في: <b>{new Date(item.falLicenseReviewedAt).toLocaleString('ar-SA')}</b>
              </div>
              {item.falLicenseRejectionReason && (
                <div className="mt-2 bg-red-50 border border-red-200 rounded p-2 text-red-800">
                  <b>سبب الرفض:</b><br/>{item.falLicenseRejectionReason}
                </div>
              )}
            </div>
          )}

          {/* مودال الرفض */}
          {showReject && (
            <div className="bg-red-50 border-2 border-red-300 rounded-xl p-3">
              <div className="text-sm font-bold text-red-800 mb-2">سبب الرفض (يصل للمستخدم بالإيميل)</div>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                placeholder="مثلاً: رقم الرخصة غير موجود في بوابة الهيئة، أو الاسم غير مطابق، أو الرخصة منتهية…"
                className="w-full px-3 py-2 rounded-lg border-2 border-red-200 focus:border-red-500 outline-none bg-white text-sm"
              />
              <div className="flex justify-end gap-2 mt-2">
                <Button size="sm" variant="ghost" onClick={() => { setShowReject(false); setRejectReason(''); }}>إلغاء</Button>
                <Button size="sm" variant="primary"
                  className="!bg-red-600 hover:!bg-red-700"
                  onClick={() => reject.mutate()}
                  loading={reject.isPending}
                  disabled={rejectReason.trim().length < 5}
                >
                  ✕ تأكيد الرفض
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* الأزرار السفلية */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-2xl flex justify-between flex-wrap gap-2">
          <Button variant="ghost" onClick={onClose}>إغلاق</Button>
          {isPending && !showReject && (
            <div className="flex gap-2">
              <Button variant="primary" className="!bg-red-600 hover:!bg-red-700" onClick={() => setShowReject(true)}>
                ✕ رفض الرخصة
              </Button>
              <Button
                variant="primary"
                className={verified ? '!bg-green-600 hover:!bg-green-700' : ''}
                onClick={() => verified && approve.mutate()}
                disabled={!verified || approve.isPending}
                loading={approve.isPending}
              >
                {verified ? '✓ اعتماد الرخصة' : '✓ اعتماد (أكّد التحقق أولاً)'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Helpers ━━━━━━━━━━━━━━━━━━━━

function Field({ label, value, dir, copyable, highlight }: {
  label: string; value: string; dir?: string; copyable?: boolean; highlight?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className={`bg-gray-50 border border-gray-200 rounded-lg p-2 ${highlight ? 'border-gold/40 bg-gold-soft' : ''}`}>
      <div className="text-[10px] text-ink-muted mb-0.5">{label}</div>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-xs font-bold ${highlight ? 'text-gold-dark' : 'text-ink'}`} dir={dir}>{value}</span>
        {copyable && value !== '—' && (
          <button
            onClick={() => {
              navigator.clipboard.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 1200);
            }}
            className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-gray-200 hover:bg-navy-soft"
          >
            {copied ? '✓' : '📋'}
          </button>
        )}
      </div>
    </div>
  );
}

function StatusCard({ label, value, color, icon, active, onClick, pulse }: {
  label: string; value: number; color: 'amber' | 'green' | 'red' | 'gray';
  icon: string; active?: boolean; onClick: () => void; pulse?: boolean;
}) {
  const colors = {
    amber: { active: 'border-amber-400 bg-amber-50', text: 'text-amber-700' },
    green: { active: 'border-green-400 bg-green-50', text: 'text-green-700' },
    red:   { active: 'border-red-400 bg-red-50',     text: 'text-red-700' },
    gray:  { active: 'border-gray-400 bg-gray-100',  text: 'text-gray-700' },
  }[color];
  return (
    <button onClick={onClick} className={`text-right p-3 rounded-2xl border-2 bg-white transition hover:shadow-brand ${active ? colors.active : 'border-gray-200'}`}>
      <div className="flex items-center justify-between">
        <span className="text-xl">{icon}</span>
        {pulse && value > 0 && <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />}
      </div>
      <div className={`text-2xl font-black mt-1 ${colors.text}`}>{value}</div>
      <div className="text-[11px] text-ink-muted">{label}</div>
    </button>
  );
}
